import React, { FunctionComponent, useEffect, useState } from 'react';
import {
  ComboBox,
  DefaultButton,
  Dialog,
  DialogFooter,
  Dropdown,
  getTheme,
  IComboBox,
  Stack,
  Text,
  TextField
} from '@fluentui/react';
import { SharedColors } from '@fluentui/theme';

import { settingsDropdownRoleStyles, settingsFieldStyles } from '../SettingsStyles';
import { RoleDropDownOptions } from './EditGroupRoleDialog';
import { getGroupId, insertGroupRole, RoleType, Group } from '../../../services/groupRoles.service';
import { SalusPrimaryButton } from '../../shared/SalusPrimaryButton';
import { AdminContext } from '../../../Store/Admin';
import { AxiosError } from 'axios';

const NumberOfGroupsToShow = 10;

interface AddGroupRoleDialogProps {
  showAddGroupRoleDialog: boolean;
  setShowAddGroupRoleDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AddGroupRoleDialog: FunctionComponent<AddGroupRoleDialogProps> = (props): JSX.Element => {
  const { palette, fonts } = getTheme();
  const { state } = React.useContext(AdminContext);
  const [groupNameErrorMessage, setGroupNameErrorMessage] = useState('');
  const [groupIdErrorMessage, setGroupIdErrorMessage] = useState('');
  const [groupName, setGroupName] = useState('');
  const [groupSearchString, setGroupSearchString] = useState('');
  const [groupId, setGroupId] = useState('');
  const [groups, setGroups] = useState<Group[]>();
  const [groupCreated, setGroupCreated] = useState(false);
  const [role, setRole] = useState<RoleType>();
  const [debounceTimer, setDebounceTimer] = useState<number>();
  const [addGroupRoleErrorMessage, setAddGroupRoleErrorMessage] = useState('');

  const debounceSearch = (func: () => void, timeout = 300) => {
    return () => {
      // When this is called (which happens when anything is typed) we reset the debounce timer
      clearTimeout(debounceTimer);
      const timer = setTimeout(() => {
        // when the debounce timer expires (after a length of timeout has passed without a rest)
        // then we execute a search
        func();
      }, timeout);
      // Small todo, check and see if we can avoid this unknown
      setDebounceTimer((timer as unknown) as number);
    };
  };

  const onSearchGroups = () => {
    // Search for possible id by name
    getGroupId(groupSearchString)
      .then(groupsResponse => {
        setGroups(groupsResponse);

        if (!groupsResponse.length) {
          setGroupNameErrorMessage('Group does not exist in database, confirm spelling');
        } else {
          setGroupNameErrorMessage('');
          onOpenClick();
        }
      })
      .catch(() => {
        setGroupNameErrorMessage('Group does not exist in database, confirm spelling');
      });
  };

  const onAddGroup = () => {
    if (!state.queueDataState || !state.tenantId || !role) {
      return;
    }

    const newGroupRole = {
      groupId: groupId,
      groupName: groupName,
      tenantId: state.tenantId,
      titleId: state.queueDataState?.title.titleId,
      subjectType: state.queueDataState?.subjectType,
      role: role
    };

    insertGroupRole(newGroupRole)
      .then(() => {
        setGroupCreated(true);
        setAddGroupRoleErrorMessage('');
      })
      .catch((err: AxiosError) => {
        const responseData = err.response?.data;
        setAddGroupRoleErrorMessage(
          typeof responseData === 'string' ? responseData : 'Failed to add role, try again then contact support.'
        );
      });
  };

  useEffect(() => {
    if (groupSearchString) {
      debounceSearch(onSearchGroups)();
    }
  }, [groupSearchString]);

  const onCloseDialog = () => {
    props.setShowAddGroupRoleDialog(false);
  };

  const comboBoxRef = React.useRef<IComboBox>(null);
  const onOpenClick = React.useCallback(() => comboBoxRef.current?.focus(true), []);
  return (
    <Dialog
      hidden={!props.showAddGroupRoleDialog}
      dialogContentProps={{
        title: 'Add AAD security group',
        titleProps: {
          'aria-level': 2
        },
        subText: groupCreated
          ? 'Group created successfully'
          : 'Enter a group ID directly, or enter a user group name and select group from the dropdown. Then, choose a role.'
      }}
      modalProps={{
        overlay: {
          isDarkThemed: true
        },
        isBlocking: false
      }}
    >
      {!groupCreated && (
        <>
          <Stack>
            <ComboBox
              autoComplete={'off'}
              componentRef={comboBoxRef}
              label="Search for a group:"
              allowFreeform={true}
              onInputValueChange={val => {
                setGroupSearchString(val ?? '');
              }}
              onChange={(_, val) => {
                if (val?.key) {
                  setGroupName(val.text ?? '');
                  setGroupId(val.key.toLocaleString() ?? '');
                  setGroupIdErrorMessage('');
                  setAddGroupRoleErrorMessage('');
                  setGroupNameErrorMessage('');
                }
              }}
              placeholder={'Enter a group name'}
              errorMessage={groupNameErrorMessage}
              styles={settingsFieldStyles(palette, fonts, '100%')}
              options={
                [...(groups || [])].splice(0, NumberOfGroupsToShow)?.map(group => {
                  return { key: group.id, text: group.name };
                }) || []
              }
            />
          </Stack>

          <Stack style={{ marginBottom: 18, marginTop: 18 }}>
            <TextField
              label="Group Name"
              value={groupName}
              disabled
              placeholder={'Select a group from the drop down above'}
              styles={settingsFieldStyles(palette, fonts, '100%')}
            />
          </Stack>

          <Stack style={{ marginBottom: 18, marginTop: 18 }}>
            <TextField
              label="Group ID"
              value={groupId}
              onChange={(_, newValue) => {
                setGroupId(newValue ?? '');
                if (newValue?.length !== 36) {
                  setGroupIdErrorMessage('Group ID must be a properly formatted GUID');
                } else {
                  setGroupIdErrorMessage('');
                  setAddGroupRoleErrorMessage('');
                }
              }}
              placeholder={'Enter a group id number'}
              errorMessage={groupIdErrorMessage}
              styles={settingsFieldStyles(palette, fonts, '100%')}
              required
            />
          </Stack>

          <Stack style={{ marginBottom: 36 }}>
            <Dropdown
              label={'Role'}
              placeholder={'Select a role'}
              options={RoleDropDownOptions}
              styles={settingsDropdownRoleStyles(palette, fonts, '100%')}
              onChange={(e, option): void => {
                setRole(option?.key as RoleType);
                setAddGroupRoleErrorMessage('');
              }}
              required
            />
          </Stack>
        </>
      )}

      <DialogFooter>
        {!groupCreated && (
          <SalusPrimaryButton disabled={!role || !groupId || groupId.length !== 36} onClick={onAddGroup} text="Add" />
        )}
        <DefaultButton onClick={onCloseDialog} text={groupCreated ? 'Done' : 'Cancel'} />
        {addGroupRoleErrorMessage && (
          <Stack>
            <Text style={{ textAlign: 'center', marginTop: 20, color: SharedColors.red20 }}>
              {addGroupRoleErrorMessage}
            </Text>
          </Stack>
        )}
      </DialogFooter>
    </Dialog>
  );
};
